import { usePageTitle } from "../hooks/usePageTitle";
import { Star, ArrowRight, TrendingUp, MessageSquare } from "lucide-react";
import { FadeUp, CardHover } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";

const CUSTOMERS = [
  {
    name: "Jake M.",
    business: "Jake's Exterior Services",
    trade: "Exterior Cleaning",
    city: "Columbus, OH",
    before: "Losing leads to faster competitors. No follow-up system.",
    after: "+3 jobs booked in the first week",
    stat: "+$840",
    statLabel: "recovered revenue, week one",
    quote: "I booked 3 jobs in the first week that I know I would have lost before. The follow-up alone paid for itself.",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
    tradeColor: "green",
  },
  {
    name: "Maria S.",
    business: "Maria's Clean House",
    trade: "Residential Cleaning",
    city: "Austin, TX",
    before: "12 Google reviews after 4 years in business.",
    after: "47 reviews in 60 days",
    stat: "4x",
    statLabel: "more reviews in 60 days",
    quote: "We went from 12 Google reviews to 47 in two months. CleanBid just asked at the right time.",
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face",
    tradeColor: "indigo",
  },
  {
    name: "Derek P.",
    business: "Derek's Lawn & Landscape",
    trade: "Lawn Care",
    city: "Atlanta, GA",
    before: "Running entire business from a personal cell phone. No system.",
    after: "+6 jobs closed in the first month",
    stat: "+$1,200",
    statLabel: "additional revenue, month one",
    quote: "It's the first piece of software that actually feels like it's on my side.",
    img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
    tradeColor: "green",
  },
];

export default function CustomersPage() {
  usePageTitle("Customer Stories | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20" data-testid="customers-page">
      {/* Hero */}
      <section className="py-24 text-center">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <FadeUp>
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">Customers</span>
          </FadeUp>
          <FadeUp delay={60}>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-[#F4F4F8] mt-4 mb-5">
              Real businesses.{" "}
              <span className="text-gradient-green">Real results.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={120}>
            <p className="text-lg text-[#8B8BA7] max-w-2xl mx-auto">
              Owner-operators across home services using CleanBid to close faster,
              follow up automatically, and build their reputation on autopilot.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-t border-b border-[#1E1E2E]" data-testid="customers-stats">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { val: "200+", label: "Service businesses" },
              { val: "50K+", label: "Quotes sent" },
              { val: "4.2x", label: "More reviews" },
              { val: "47%", label: "More booked jobs" },
            ].map((s, i) => (
              <FadeUp key={i} delay={i * 60}>
                <p className="text-3xl md:text-4xl font-bold text-[#F4F4F8]">{s.val}</p>
                <p className="text-sm text-[#8B8BA7] mt-1">{s.label}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Customer stories */}
      <section className="py-24" data-testid="customer-stories">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="space-y-8">
            {CUSTOMERS.map((c, i) => (
              <FadeUp key={i}>
                <CardHover>
                <div className="bg-[#111118] border border-[#1E1E2E] rounded-3xl p-8 md:p-10 transition-[border-color] duration-300 hover:border-[#71DED6]/20"
                  data-testid={`customer-story-${i}`}
                >

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                    {/* Left: identity */}
                    <div className="md:col-span-1">
                      <div className="flex items-start gap-4">
                        <img
                          src={c.img}
                          alt={c.name}
                          className="w-14 h-14 rounded-2xl object-cover bg-[#1E1E2E] flex-shrink-0"
                        />
                        <div>
                          <p className="font-bold text-[#F4F4F8] text-lg">{c.name}</p>
                          <p className="text-sm text-[#8B8BA7]">{c.business}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                                c.tradeColor === "green"
                                  ? "bg-[#71DED6]/15 text-[#71DED6]"
                                  : "bg-[#6B5CF6]/15 text-[#6B5CF6]"
                              }`}
                            >
                              {c.trade}
                            </span>
                            <span className="text-[10px] text-[#8B8BA7] px-2.5 py-1 bg-[#1E1E2E] rounded-full">
                              {c.city}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Before/After */}
                      <div className="mt-6 space-y-3">
                        <div className="bg-[#0A0A0F] border border-[#1E1E2E] rounded-xl p-4">
                          <p className="text-[10px] text-[#8B8BA7] uppercase tracking-widest mb-1">Before</p>
                          <p className="text-sm text-[#8B8BA7]">{c.before}</p>
                        </div>
                        <div className="bg-[#71DED6]/5 border border-[#71DED6]/20 rounded-xl p-4">
                          <p className="text-[10px] text-[#71DED6] uppercase tracking-widest mb-1">After</p>
                          <p className="text-sm text-[#F4F4F8] font-medium">{c.after}</p>
                        </div>
                      </div>
                    </div>

                    {/* Middle: stat */}
                    <div className="md:col-span-1 flex flex-col items-center justify-center py-6 md:py-0 border-t border-b md:border-t-0 md:border-b-0 md:border-l md:border-r border-[#1E1E2E] md:px-8">
                      <div className="text-center">
                        <TrendingUp className="w-8 h-8 text-[#71DED6] mx-auto mb-3 opacity-60" />
                        <p className="text-5xl font-bold text-[#71DED6] tracking-tight">{c.stat}</p>
                        <p className="text-sm text-[#8B8BA7] mt-2 max-w-[160px] mx-auto">{c.statLabel}</p>
                      </div>
                    </div>

                    {/* Right: quote */}
                    <div className="md:col-span-1 flex flex-col justify-center">
                      <div className="flex gap-0.5 mb-4">
                        {[...Array(5)].map((_, j) => (
                          <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <blockquote className="text-[#F4F4F8] text-lg leading-relaxed font-medium">
                        "{c.quote}"
                      </blockquote>
                      <p className="text-sm text-[#8B8BA7] mt-4">— {c.name}, {c.trade}</p>
                    </div>
                  </div>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Your story section */}
      <section className="py-20 bg-[#111118]/40 border-t border-[#1E1E2E]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <FadeUp>
            <MessageSquare className="w-10 h-10 text-[#71DED6] mx-auto mb-4 opacity-60" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8] mb-3">
              Your story starts with a demo.
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] text-lg mb-8 max-w-xl mx-auto">
              Every customer story starts the same way — a 20-minute call that shows exactly
              how CleanBid works for their business.
            </p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="customers-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-flex items-center gap-2"
            >
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
